import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLang } from "../../../shared/i18n/useLang";
import AuthShell, { PasswordField, erroresDeCampo } from "../components/AuthShell";
import { confirmarReset, validarReset } from "../services/accesoService";

/**
 * /restablecer/:uid/:token — elegir contraseña nueva desde el link del correo.
 * Primero se valida el link, así quien llega con uno vencido se entera antes
 * de escribir nada.
 */
export default function RestablecerPage() {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const L = useLang();

  const [estado, setEstado] = useState({ cargando: true, valido: false, username: "", error: "" });
  const [password, setPassword] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    validarReset(uid, token)
      .then((d) => setEstado({ cargando: false, valido: true, username: d.username, error: "" }))
      .catch((e) => setEstado({ cargando: false, valido: false, username: "", error: e.message }));
  }, [uid, token]);

  async function enviar(ev) {
    ev.preventDefault();
    setApiError("");
    const e = {};
    if (password.length < 8) e.password = L("Mínimo 8 caracteres.", "At least 8 characters.");
    if (confirmar !== password) e.confirmar = L("Las contraseñas no coinciden.", "Passwords do not match.");
    setErrors(e);
    if (Object.keys(e).length) return;

    setEnviando(true);
    try {
      const r = await confirmarReset(uid, token, password);
      navigate("/login", {
        replace: true,
        state: {
          username: r.username,
          aviso: L("Contraseña actualizada. Ya podés iniciar sesión.", "Password updated. You can sign in now."),
        },
      });
    } catch (err) {
      if (err.status === 400 && err.data?.password) setErrors(erroresDeCampo(err.data));
      else setApiError(err.message);
    } finally {
      setEnviando(false);
    }
  }

  if (estado.cargando) {
    return (
      <AuthShell>
        <p className="lp-form-sub" style={{ textAlign: "center", padding: "30px 0" }}>
          <i className="fa-solid fa-circle-notch lp-spin" /> {L("Verificando enlace…", "Checking link…")}
        </p>
      </AuthShell>
    );
  }

  if (!estado.valido) {
    return (
      <AuthShell>
        <h2 className="lp-form-title">{L("Enlace no válido", "Invalid link")}</h2>
        <div className="lp-api-err" style={{ marginTop: 14 }}>
          <i className="fa-solid fa-circle-exclamation" />
          {estado.error}
        </div>
        <Link to="/recuperar" className="lp-btn" style={{ textDecoration: "none", display: "flex", justifyContent: "center" }}>
          {L("Pedir un enlace nuevo", "Request a new link")}
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="lp-chip">
        <i className="fa-solid fa-user" />
        {estado.username}
      </div>
      <h2 className="lp-form-title">{L("Nueva contraseña", "New password")}</h2>
      <p className="lp-form-sub">{L("Elegí la contraseña con la que vas a entrar.", "Choose the password you'll sign in with.")}</p>

      {apiError && (
        <div className="lp-api-err">
          <i className="fa-solid fa-circle-exclamation" />
          {apiError}
        </div>
      )}

      <form onSubmit={enviar} noValidate>
        {/* Campo oculto: ayuda a los gestores de contraseñas a guardar la
            clave nueva con el usuario correcto. */}
        <input type="text" name="username" value={estado.username} autoComplete="username" readOnly hidden />
        <PasswordField id="rs-pass" label={L("Contraseña nueva", "New password")} value={password}
          onChange={(v) => { setPassword(v); setErrors((x) => ({ ...x, password: "" })); }}
          error={errors.password} disabled={enviando}
          show={showPass} onToggle={() => setShowPass((v) => !v)}
          hint={L("Mínimo 8 caracteres. Evitá contraseñas comunes o solo números.",
                  "At least 8 characters. Avoid common or all-numeric passwords.")} />
        <PasswordField id="rs-pass2" label={L("Confirmar contraseña", "Confirm password")} value={confirmar}
          onChange={(v) => { setConfirmar(v); setErrors((x) => ({ ...x, confirmar: "" })); }}
          error={errors.confirmar} disabled={enviando}
          show={showPass} onToggle={() => setShowPass((v) => !v)} />
        <button type="submit" className="lp-btn" disabled={enviando}>
          {enviando
            ? <><i className="fa-solid fa-circle-notch lp-spin" /> {L("Guardando…", "Saving…")}</>
            : L("Guardar contraseña", "Save password")}
        </button>
      </form>
    </AuthShell>
  );
}
